import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Eye, EyeOff, Check } from "lucide-react";
import { Logo1895 } from "@/components/Logo1895";
import { IconVault } from "@/components/Icons";
import { useAuth } from "@/contexts/AuthContext";
import {
  getVaultConfig,
  initializeVault,
  updateVaultConfig,
  isVaultUnlocked,
  isECDHReady,
  lockVault,
} from "@/services/vaultService";

const TIERS = [
  {
    id: "cloud",
    name: "Standard",
    tagline: "For indie writers and small teams",
    description:
      "Your projects are stored normally. Data is encrypted in transit and at rest on our servers. AI features are powered by our infrastructure — pay per credit.",
    features: [
      "All AI features included",
      "Pay-per-credit generation",
      "TLS + server-side encryption",
      "Collaboration support",
    ],
    badge: null,
  },
  {
    id: "vault",
    name: "1895 Vault",
    tagline: "For production companies and studios",
    description:
      "True zero-knowledge encryption. Scripts are encrypted in your browser before reaching our servers. AI calls go directly from your browser to Claude and Gemini using your own API keys. We never see your content.",
    features: [
      "Client-side AES-256-GCM encryption",
      "Zero-knowledge — we can't read your data",
      "Your own Claude + Gemini API keys",
      "Per-project encryption keys",
      "GDPR right-to-deletion built in",
    ],
    badge: "Premium",
  },
];

export default function VaultSettings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showGeminiKey, setShowGeminiKey] = useState(false);
  const [showClaudeKey, setShowClaudeKey] = useState(false);

  // Current state from backend
  const [vaultExists, setVaultExists] = useState(false);
  const [currentTier, setCurrentTier] = useState("cloud");

  // Form state
  const [selectedTier, setSelectedTier] = useState("cloud");
  const [vaultPassword, setVaultPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [geminiApiKey, setGeminiApiKey] = useState("");
  const [claudeApiKey, setClaudeApiKey] = useState("");

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const config = await getVaultConfig();
      if (config.exists) {
        setVaultExists(true);
        setCurrentTier(config.security_tier);
        setSelectedTier(config.security_tier);
      }
    } catch (err) {
      console.error("Failed to load vault config:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (selectedTier === "vault") {
      if (!vaultExists && !vaultPassword) {
        toast.error("Vault password is required to enable encryption");
        return;
      }
      if (!vaultExists && vaultPassword !== confirmPassword) {
        toast.error("Passwords do not match");
        return;
      }
      if (!vaultExists && vaultPassword.length < 8) {
        toast.error("Vault password must be at least 8 characters");
        return;
      }
      if (!claudeApiKey) {
        toast.error("Claude API key is required for Vault mode (used for Greenlight analysis)");
        return;
      }
      if (!geminiApiKey) {
        toast.error("Gemini API key is required for Vault mode (used for image generation)");
        return;
      }
    }

    setSaving(true);
    try {
      if (selectedTier === "cloud") {
        await updateVaultConfig("cloud", null);
        lockVault();
        setCurrentTier("cloud");
        toast.success("Security set to Standard");
      } else {
        const byokConfig = { geminiApiKey, claudeApiKey };

        if (!vaultExists) {
          await initializeVault(vaultPassword, null, "vault", byokConfig);
          setVaultExists(true);
        } else {
          await updateVaultConfig("vault", byokConfig);
        }

        setCurrentTier("vault");
        toast.success("1895 Vault enabled");
      }
    } catch (err) {
      console.error("Vault setup error:", err);
      toast.error("Failed to update security settings");
    } finally {
      setSaving(false);
    }
  };

  const needsPassword = selectedTier === "vault" && !vaultExists;
  const hasChanges = selectedTier !== currentTier;

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-app)] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--text-muted)]" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[var(--bg-app)]">
      {/* Header */}
      <header className="shrink-0 bg-[rgba(9,9,11,0.8)] backdrop-blur-xl border-b border-[var(--border-subtle)] z-50">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            >
              <ArrowLeft size={18} className="mr-1" />
              Back
            </Button>
            <div className="h-6 w-px bg-[var(--border-subtle)]" />
            <div className="flex items-center gap-2">
              <IconVault size={22} active />
              <h1 className="text-lg font-semibold text-[var(--text-primary)]">
                Security
              </h1>
            </div>
          </div>
          <Logo1895 variant="mark" width={28} className="opacity-40" />
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-10">
          {/* Hero */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
              1895 Vault
            </h2>
            <p className="text-[var(--text-muted)] text-sm max-w-xl">
              Choose how your scripts and project data are protected.
            </p>
          </div>

          {/* Tier Cards */}
          <div className="grid gap-4 mb-8">
            {TIERS.map((tier) => {
              const isSelected = selectedTier === tier.id;
              const isCurrent = currentTier === tier.id;
              return (
                <motion.button
                  key={tier.id}
                  onClick={() => setSelectedTier(tier.id)}
                  whileTap={{ scale: 0.995 }}
                  className={`relative text-left w-full rounded-2xl border p-5 transition-all duration-200 ${
                    isSelected
                      ? "border-amber-500/50 bg-amber-500/5"
                      : "border-[var(--border-subtle)] bg-[var(--bg-elevated)] hover:border-[var(--border-default)]"
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                          isSelected
                            ? "border-amber-500"
                            : "border-[var(--border-default)]"
                        }`}
                      >
                        {isSelected && (
                          <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-[var(--text-primary)]">
                            {tier.name}
                          </span>
                          {tier.badge && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 uppercase tracking-wider">
                              {tier.badge}
                            </span>
                          )}
                          {isCurrent && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 uppercase tracking-wider">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-[var(--text-muted)] mt-0.5">
                          {tier.tagline}
                        </p>
                      </div>
                    </div>
                    {tier.id === "vault" && (
                      <IconVault size={28} active={isSelected} />
                    )}
                  </div>

                  <p className="text-xs text-[var(--text-secondary)] mb-3 ml-8">
                    {tier.description}
                  </p>

                  <div className="flex flex-wrap gap-x-4 gap-y-1 ml-8">
                    {tier.features.map((f) => (
                      <span
                        key={f}
                        className="flex items-center gap-1.5 text-[11px] text-[var(--text-muted)]"
                      >
                        <Check
                          size={10}
                          className={
                            isSelected
                              ? "text-amber-400"
                              : "text-[var(--text-disabled)]"
                          }
                        />
                        {f}
                      </span>
                    ))}
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Configuration Panel — only for vault tier */}
          {selectedTier === "vault" && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-6 mb-8 space-y-6"
            >
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                Vault Configuration
              </h3>

              {/* Vault Password — only for first-time setup */}
              {needsPassword && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-[var(--text-secondary)] text-xs">
                      Vault Password
                    </Label>
                    <p className="text-[10px] text-[var(--text-disabled)] mb-2">
                      This password encrypts your data. If you forget it, your
                      encrypted projects cannot be recovered.
                    </p>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={vaultPassword}
                        onChange={(e) => setVaultPassword(e.target.value)}
                        placeholder="Minimum 8 characters"
                        className="bg-[var(--bg-app)] border-[var(--border-default)] text-[var(--text-primary)] pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-disabled)] hover:text-[var(--text-secondary)]"
                      >
                        {showPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[var(--text-secondary)] text-xs">
                      Confirm Password
                    </Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter vault password"
                      className="bg-[var(--bg-app)] border-[var(--border-default)] text-[var(--text-primary)]"
                    />
                  </div>
                </div>
              )}

              {/* API Keys */}
              <div className="space-y-4">
                <p className="text-[10px] text-[var(--text-disabled)]">
                  Your keys are stored in browser memory only — never persisted
                  or sent to 1895 servers. All AI calls go directly from your
                  browser to the providers.
                </p>

                {/* Claude API Key — for Greenlight analysis */}
                <div>
                  <Label className="text-[var(--text-secondary)] text-xs">
                    Anthropic Claude API Key
                  </Label>
                  <p className="text-[10px] text-[var(--text-disabled)] mb-1">
                    Used for Greenlight Engine script analysis
                  </p>
                  <div className="relative">
                    <Input
                      type={showClaudeKey ? "text" : "password"}
                      value={claudeApiKey}
                      onChange={(e) => setClaudeApiKey(e.target.value)}
                      placeholder="sk-ant-..."
                      className="bg-[var(--bg-app)] border-[var(--border-default)] text-[var(--text-primary)] pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowClaudeKey(!showClaudeKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-disabled)] hover:text-[var(--text-secondary)]"
                    >
                      {showClaudeKey ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                  </div>
                  <p className="text-[10px] text-[var(--text-disabled)] mt-1">
                    Get a key at{" "}
                    <a
                      href="https://console.anthropic.com/settings/keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-amber-400/70 hover:text-amber-400 underline underline-offset-2"
                    >
                      console.anthropic.com
                    </a>
                  </p>
                </div>

                {/* Gemini API Key — for image generation */}
                <div>
                  <Label className="text-[var(--text-secondary)] text-xs">
                    Google Gemini API Key
                  </Label>
                  <p className="text-[10px] text-[var(--text-disabled)] mb-1">
                    Used for storyboard and image generation
                  </p>
                  <div className="relative">
                    <Input
                      type={showGeminiKey ? "text" : "password"}
                      value={geminiApiKey}
                      onChange={(e) => setGeminiApiKey(e.target.value)}
                      placeholder="AIza..."
                      className="bg-[var(--bg-app)] border-[var(--border-default)] text-[var(--text-primary)] pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowGeminiKey(!showGeminiKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-disabled)] hover:text-[var(--text-secondary)]"
                    >
                      {showGeminiKey ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                  </div>
                  <p className="text-[10px] text-[var(--text-disabled)] mt-1">
                    Get a key at{" "}
                    <a
                      href="https://aistudio.google.com/apikey"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-amber-400/70 hover:text-amber-400 underline underline-offset-2"
                    >
                      aistudio.google.com/apikey
                    </a>
                  </p>
                </div>
              </div>

              {/* Vault already active indicator */}
              {vaultExists && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs text-emerald-400">
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                    Vault is active.{" "}
                    {isVaultUnlocked()
                      ? "Keys are in memory."
                      : "Locked — unlock on next login."}
                  </div>
                  {isVaultUnlocked() && isECDHReady() && (
                    <div className="flex items-center gap-2 text-xs text-emerald-400/70 ml-4">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/50" />
                      Per-section encryption with ECDH sharing enabled
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* Save Button */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-[var(--text-disabled)]">
              {selectedTier === "cloud"
                ? "Standard protection — no client-side encryption"
                : "AES-256-GCM, PBKDF2 600k iterations, per-project keys"}
            </p>
            <Button
              onClick={handleSave}
              disabled={saving || (!hasChanges && !needsPassword && selectedTier !== "vault")}
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-6"
            >
              {saving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : hasChanges || needsPassword ? (
                "Save Changes"
              ) : (
                "Saved"
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
